import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Copy, Loader2, Power } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function InstallQr({ build, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const code = build.install_code;
  const url = code ? `${window.location.origin}/api/i/${code}` : "";
  const short = url.replace(/^https?:\/\//, "");

  const toggle = async (on) => {
    setBusy(true);
    try {
      const r = on ? await api.post(`/channel-builder/builds/${build.id}/install-link`) : await api.delete(`/channel-builder/builds/${build.id}/install-link`);
      onChange(r.data.install_code);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the install link"); } finally { setBusy(false); }
  };
  const openDialog = async () => { setOpen(true); if (!code) await toggle(true); };
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Couldn't copy"));

  return (
    <>
      <button data-testid={`build-install-qr-${build.id}`} onClick={openDialog} title="Install on a Fire TV"
        className={"h-8 px-3 rounded-lg border text-xs inline-flex items-center gap-1.5 transition-colors " + (code ? "border-emerald-500/30 text-emerald-300 hover:border-emerald-500/60" : "border-gray-800 text-gray-300 hover:text-amber-400 hover:border-amber-500/40")}>
        <QrCode className="h-3.5 w-3.5" /> Install
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} data-testid="install-qr-dialog" className="bg-card border-gray-800 max-w-md">
          <DialogHeader><DialogTitle className="font-display">Install {build.church_name} v{build.version}</DialogTitle></DialogHeader>
          {!code ? (
            <div className="py-10 flex flex-col items-center gap-3 text-sm text-gray-400">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <>
                <p>The install link is turned off.</p>
                <button data-testid="install-link-enable" onClick={() => toggle(true)} className="h-9 px-4 rounded-lg gold-gradient text-black text-sm font-semibold">Turn on install link</button>
              </>}
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex justify-center"><div className="rounded-xl bg-white p-4" data-testid="install-qr-code"><QRCodeSVG value={url} size={200} level="M" /></div></div>
              <div className="rounded-lg bg-gray-950 border border-gray-800 p-3 text-center">
                <p className="text-[11px] text-gray-500 mb-1">Type this into the Downloader app on the Fire TV</p>
                <p data-testid="install-short-address" className="font-mono text-lg text-amber-300 break-all select-all">{short}</p>
              </div>
              <ol className="text-xs text-gray-400 space-y-1.5 list-decimal pl-4">
                <li>On the Fire TV, install the free <span className="text-gray-200">Downloader</span> app from the Amazon Appstore.</li>
                <li>Settings → My Fire TV → Developer options → Install unknown apps → turn on for Downloader.</li>
                <li>Open Downloader, type the address above and press Go. Choose Install when asked.</li>
              </ol>
              <p className="text-[11px] text-gray-500">On an Android TV or phone, scan the QR code to download the APK directly.</p>
              <div className="flex items-center justify-between pt-1">
                <button data-testid="install-link-copy" onClick={copy} className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"><Copy className="h-3.5 w-3.5" /> Copy link</button>
                <button data-testid="install-link-disable" onClick={() => toggle(false)} disabled={busy} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1 disabled:opacity-60">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />} Turn off link
                </button>
              </div>
              <p className="text-[11px] text-gray-500">Anyone with this link can download the app until you turn it off.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
